import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@offline-mui/material'
import { useEffect, useMemo, useState } from 'react'
import { createFlightsData, updateFlightsData } from '../api/inventory'
import type { FlightsData, FlightsDataPayload } from '../api/inventory'
import { toDateTimeLocalInput } from '../utils/flightFormat'
import { DateTimeField } from './ui/DateField'

const AIRPORTS = ['CDG', 'CLT', 'JFK', 'LAX', 'LHR', 'ORD', 'PHL', 'SFO', 'YYZ']
const CARRIERS = ['AA', 'AC', 'BA', 'DL', 'UA']

export type FlightDataFormState = {
  itineraryId: string
  segmentId: string
  flightOrder: string
  flightNumber: string
  carrier: string
  operatingCarrier: string
  operatingCarrierName: string
  departureAirport: string
  departureDatetime: string
  arrivalAirport: string
  arrivalDatetime: string
  aircraftType: string
  durationMin: string
}

const EMPTY_FORM: FlightDataFormState = {
  itineraryId: '',
  segmentId: '',
  flightOrder: '1',
  flightNumber: '',
  carrier: '',
  operatingCarrier: '',
  operatingCarrierName: '',
  departureAirport: '',
  departureDatetime: '',
  arrivalAirport: '',
  arrivalDatetime: '',
  aircraftType: '',
  durationMin: '',
}

function toFormState(flight: FlightsData): FlightDataFormState {
  return {
    itineraryId: String(flight.itinerary_id),
    segmentId: String(flight.segment_id),
    flightOrder: String(flight.flight_order),
    flightNumber: flight.flight_number,
    carrier: flight.carrier,
    operatingCarrier: flight.operating_carrier || '',
    operatingCarrierName: flight.operating_carrier_name || '',
    departureAirport: flight.departure_airport,
    departureDatetime: toDateTimeLocalInput(flight.departure_datetime),
    arrivalAirport: flight.arrival_airport,
    arrivalDatetime: toDateTimeLocalInput(flight.arrival_datetime),
    aircraftType: flight.aircraft_type || '',
    durationMin: String(flight.duration_min),
  }
}

function toPayload(form: FlightDataFormState): FlightsDataPayload {
  return {
    itineraryId: Number(form.itineraryId),
    segmentId: Number(form.segmentId),
    flightOrder: Number(form.flightOrder),
    flightNumber: form.flightNumber.trim(),
    carrier: form.carrier.trim().toUpperCase(),
    operatingCarrier: form.operatingCarrier.trim().toUpperCase() || undefined,
    operatingCarrierName: form.operatingCarrierName.trim() || undefined,
    departureAirport: form.departureAirport.trim().toUpperCase(),
    departureDatetime: form.departureDatetime,
    arrivalAirport: form.arrivalAirport.trim().toUpperCase(),
    arrivalDatetime: form.arrivalDatetime,
    aircraftType: form.aircraftType.trim() || undefined,
    durationMin: Number(form.durationMin),
  }
}

type FlightDataDialogProps = {
  open: boolean
  editing?: FlightsData | null
  initialValues?: Partial<FlightDataFormState>
  onClose: () => void
  onSaved: (flight: FlightsData) => void
}

export default function FlightDataDialog({
  open,
  editing = null,
  initialValues,
  onClose,
  onSaved,
}: FlightDataDialogProps) {
  const [form, setForm] = useState<FlightDataFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setFormError(null)
    if (editing) {
      setForm(toFormState(editing))
      return
    }
    setForm({
      ...EMPTY_FORM,
      ...(initialValues ?? {}),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing])

  const formValid = useMemo(() => {
    return Boolean(
      form.itineraryId &&
        form.segmentId &&
        form.flightOrder &&
        form.flightNumber &&
        form.carrier &&
        form.departureAirport &&
        form.departureDatetime &&
        form.arrivalAirport &&
        form.arrivalDatetime &&
        form.durationMin &&
        Number(form.durationMin) > 0,
    )
  }, [form])

  const handleSave = async () => {
    if (!formValid) return
    setSaving(true)
    setFormError(null)
    try {
      const payload = toPayload(form)
      const result = editing
        ? await updateFlightsData(editing.id, payload)
        : await createFlightsData(payload)
      onSaved(result.data)
      onClose()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{editing ? `Edit flight #${editing.id}` : 'Add flight'}</DialogTitle>
      <DialogContent dividers>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              type="number"
              label="Block ID"
              value={form.itineraryId}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, itineraryId: event.target.value }))
              }
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              type="number"
              label="Segment ID"
              value={form.segmentId}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, segmentId: event.target.value }))
              }
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              type="number"
              label="Flight order"
              value={form.flightOrder}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, flightOrder: event.target.value }))
              }
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              label="Flight number"
              value={form.flightNumber}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, flightNumber: event.target.value }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Autocomplete
              options={CARRIERS}
              value={form.carrier || null}
              onChange={(_, value) =>
                setForm((prev) => ({ ...prev, carrier: value ?? '' }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Carrier" required />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              type="number"
              label="Duration (min)"
              value={form.durationMin}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, durationMin: event.target.value }))
              }
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={AIRPORTS}
              value={form.departureAirport || null}
              onChange={(_, value) =>
                setForm((prev) => ({ ...prev, departureAirport: value ?? '' }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Departure airport" required />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={AIRPORTS}
              value={form.arrivalAirport || null}
              onChange={(_, value) =>
                setForm((prev) => ({ ...prev, arrivalAirport: value ?? '' }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Arrival airport" required />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DateTimeField
              required
              label="Departure"
              value={form.departureDatetime}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, departureDatetime: value }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DateTimeField
              required
              label="Arrival"
              value={form.arrivalDatetime}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, arrivalDatetime: value }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Operating carrier"
              value={form.operatingCarrier}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  operatingCarrier: event.target.value,
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Operating carrier name"
              value={form.operatingCarrierName}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  operatingCarrierName: event.target.value,
                }))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Aircraft type"
              value={form.aircraftType}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, aircraftType: event.target.value }))
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!formValid || saving}
          onClick={() => void handleSave()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {editing ? 'Save changes' : 'Create flight'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
